// Policies (FR-8.7): HR publishes versions and sees who has acknowledged each;
// everyone acknowledges the ones addressed to them from My space. Links only:
// the documents stay in Drive.
import { useCallback, useEffect, useId, useState, type CSSProperties } from 'react';
import { Badge, Button, Checkbox, Mono, Select } from './primitives';
import { PCard } from './people';
import { DisclosedForm } from './card';
import { ConfirmDialog } from './dialog';
import { Field, TextInput } from './field';
import { InlineStatus, useToast } from './feedback';
import { fmtDayKey, orgDateKey } from '../lib/dates';
import { useOrgDates } from '../lib/orgSettings';
import { useDepartments, departmentName } from '../lib/departments';
import {
  acknowledgePolicy,
  audienceOf,
  listMyPolicies,
  loadHrPolicies,
  publishPolicy,
  retirePolicy,
  type HrPolicies,
  type MyPolicy,
  type Policy,
} from '../lib/policies';

const isLink = (v: string) => /^https?:\/\/\S+$/i.test(v.trim());

const ROW: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 'var(--hf-space-2) var(--hf-space-3)',
  padding: 'var(--hf-space-2) 0',
  borderBottom: '1px solid var(--hf-rule)',
  fontSize: 'var(--hf-text-sm)',
};
const NOTE: CSSProperties = { fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' };
const OPEN: CSSProperties = { color: 'var(--hf-accent-text)', fontSize: 'var(--hf-text-sm)' };
const TITLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'baseline',
  gap: 'var(--hf-space-2)',
  flex: '1 1 12rem',
  minWidth: 0,
  color: 'var(--hf-ink)',
  overflowWrap: 'anywhere',
};

const Open = ({ p }: { p: { drive_link: string; title: string } }) => (
  <a href={p.drive_link} target="_blank" rel="noreferrer noopener" style={OPEN}>
    open
    <span className="hf-sr-only"> {p.title} in Drive</span>
  </a>
);

/** The policies addressed to me, on My space. Nothing at all when there are none. */
export function MyPoliciesCard({ uid }: { uid: string }) {
  const toast = useToast();
  const { tz } = useOrgDates();
  const [rows, setRows] = useState<MyPolicy[] | null>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => listMyPolicies().then(setRows), []);
  useEffect(() => {
    let live = true;
    void listMyPolicies().then((r) => live && setRows(r));
    return () => {
      live = false;
    };
  }, []);

  if (rows === null)
    return (
      <PCard label="Policies">
        <InlineStatus tone="error" action={{ label: 'Retry', onClick: () => void load() }}>
          Couldn’t load your policies.
        </InlineStatus>
      </PCard>
    );
  if (!rows.length) return null;

  const acknowledge = async (p: MyPolicy) => {
    setBusy(p.id);
    const { error } = await acknowledgePolicy(p.id, uid);
    setBusy(null);
    if (error) {
      toast.push({ tone: 'error', message: `Couldn’t acknowledge ${p.title} — ${error.message}` });
      return;
    }
    toast.push({ tone: 'success', message: `Acknowledged ${p.title} (${p.version}).` });
    void load();
  };

  // What you still have to do, first.
  const shown = [...rows].sort((a, b) => Number(!!a.acknowledged_at) - Number(!!b.acknowledged_at));

  return (
    <PCard label="Policies">
      <p style={{ ...NOTE, margin: '0 0 var(--hf-space-2)' }}>
        Read each one in Drive, then acknowledge it. HR sees who has.
      </p>
      {shown.map((p) => (
        <div key={p.id} role="group" aria-label={p.title} style={ROW}>
          <span style={TITLE}>
            {p.title}
            <Mono dim>{p.version}</Mono>
          </span>
          <Open p={p} />
          {p.acknowledged_at ? (
            <Badge tone="success">
              acknowledged {fmtDayKey(orgDateKey(p.acknowledged_at, tz))}
            </Badge>
          ) : (
            <Button
              variant="accent"
              size="sm"
              icon="check"
              loading={busy === p.id}
              onClick={() => void acknowledge(p)}
            >
              Acknowledge
            </Button>
          )}
        </div>
      ))}
    </PCard>
  );
}

type Draft = { base: Policy | null };

/** HR's policies: publish, issue a new version, retire, and who has acknowledged. */
export function PoliciesCard() {
  const toast = useToast();
  const { tz } = useOrgDates();
  const departments = useDepartments();
  const opener = useId();
  const [data, setData] = useState<HrPolicies | null | undefined>(undefined);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [retiring, setRetiring] = useState<Policy | null>(null);
  const [title, setTitle] = useState('');
  const [version, setVersion] = useState('');
  const [link, setLink] = useState('');
  const [audience, setAudience] = useState<Policy['audience']>('everyone');
  const [deps, setDeps] = useState<string[]>([]);
  const [errors, setErrors] = useState<{ title?: string; version?: string; link?: string }>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => loadHrPolicies().then(setData), []);
  useEffect(() => {
    let live = true;
    void loadHrPolicies().then((d) => live && setData(d));
    return () => {
      live = false;
    };
  }, []);

  const open = (base: Policy | null) => {
    setDraft({ base });
    setTitle(base?.title ?? '');
    setVersion('');
    setLink('');
    setAudience(base?.audience ?? 'everyone');
    setDeps(base?.department_ids ?? []);
    setErrors({});
    setErr(null);
  };
  const close = () => setDraft(null);

  const publish = async () => {
    const found: typeof errors = {};
    if (!title.trim()) found.title = 'Name the policy.';
    if (!version.trim()) found.version = 'Name the version, e.g. v1 or 2026.';
    if (!link.trim()) found.link = 'Paste the Drive link to the policy.';
    else if (!isLink(link)) found.link = 'That is not a link. It should start with https://';
    setErrors(found);
    if (found.title || found.version || found.link) return;
    if (audience === 'departments' && !deps.length) {
      setErr('Choose at least one department, or send it to everyone.');
      return;
    }
    setBusy(true);
    setErr(null);
    const { error } = await publishPolicy({
      title,
      version,
      driveLink: link,
      audience,
      departmentIds: deps,
      supersedes: draft?.base?.id ?? null,
    });
    setBusy(false);
    if (error) {
      setErr(error.message);
      return;
    }
    toast.push({ tone: 'success', message: `Published ${title.trim()} (${version.trim()}).` });
    close();
    void load();
  };

  const retire = async (p: Policy) => {
    setRetiring(null);
    const r = await retirePolicy(p.id);
    if (!r.ok) {
      toast.push({
        tone: 'error',
        message: `Couldn’t retire ${p.title} — ${r.reason === 'refused' ? 'you can’t change policies.' : r.error.message}`,
      });
      return;
    }
    toast.push({
      tone: 'success',
      message: `Retired ${p.title}. Nobody is asked for it any more.`,
    });
    void load();
  };

  const audienceLabel = (p: Policy) =>
    p.audience === 'everyone'
      ? 'Everyone'
      : p.department_ids.map((d) => departmentName(departments, d) ?? 'A department').join(', ');

  return (
    <PCard
      label="Policies"
      right={
        !draft && (
          <span id={opener}>
            <Button variant="secondary" size="sm" icon="plus" onClick={() => open(null)}>
              Publish policy
            </Button>
          </span>
        )
      }
    >
      <p style={{ ...NOTE, margin: '0 0 var(--hf-space-2)' }}>
        Links only — the files stay in Drive. Everyone a policy is for is asked to acknowledge it in
        My space, and asked again for each new version.
      </p>

      {data === null && (
        <InlineStatus tone="error" action={{ label: 'Retry', onClick: () => void load() }}>
          Couldn’t load the policies.
        </InlineStatus>
      )}
      {data && !data.policies.length && <div style={NOTE}>No policies published yet.</div>}
      {data?.policies.map((p) => {
        const asked = audienceOf(p, data.people);
        const done = new Set(
          data.acks.filter((a) => a.policy_id === p.id).map((a) => a.profile_id),
        );
        const missing = asked.filter((x) => !done.has(x.id));
        return (
          <div key={p.id} role="group" aria-label={p.title} style={{ ...ROW, display: 'block' }}>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 'var(--hf-space-2) var(--hf-space-3)',
              }}
            >
              <span style={TITLE}>
                {p.title}
                <Mono dim>{p.version}</Mono>
              </span>
              <span style={NOTE}>
                {audienceLabel(p)} · {fmtDayKey(orgDateKey(p.published_at, tz))}
              </span>
              <Mono>
                {asked.length - missing.length} of {asked.length} acknowledged
              </Mono>
              <Open p={p} />
              <Button variant="ghost" size="sm" onClick={() => open(p)}>
                New version
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setRetiring(p)}>
                Retire
              </Button>
            </div>
            {missing.length > 0 && (
              <details style={{ ...NOTE, marginTop: 'var(--hf-space-1)' }}>
                <summary style={{ cursor: 'pointer' }}>Not yet: {missing.length}</summary>
                {missing.map((x) => x.full_name ?? 'Someone').join(', ')}
              </details>
            )}
          </div>
        );
      })}

      {draft && (
        <DisclosedForm
          opener={opener}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--hf-space-3)',
            marginTop: 'var(--hf-space-4)',
          }}
        >
          {err && <InlineStatus tone="error">{err}</InlineStatus>}
          {draft.base && (
            <div style={NOTE}>
              A new version of {draft.base.title} ({draft.base.version}). Everyone it is for is
              asked to acknowledge it again.
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--hf-space-3)' }}>
            <Field label="Title" required error={errors.title} style={{ flex: '2 1 14rem' }}>
              <TextInput dense value={title} onChange={setTitle} placeholder="Code of conduct" />
            </Field>
            <Field label="Version" required error={errors.version} style={{ flex: '1 1 8rem' }}>
              <TextInput dense value={version} onChange={setVersion} placeholder="v1" />
            </Field>
          </div>
          <Field label="Drive link" required error={errors.link}>
            <TextInput
              dense
              type="url"
              value={link}
              onChange={setLink}
              placeholder="https://drive.google.com/…"
            />
          </Field>
          <Field label="For">
            {(control) => (
              <Select
                {...control}
                value={audience}
                onChange={(v) => setAudience(v as Policy['audience'])}
                options={[
                  { value: 'everyone', label: 'Everyone' },
                  { value: 'departments', label: 'Chosen departments' },
                ]}
              />
            )}
          </Field>
          {audience === 'departments' && (
            <div
              role="group"
              aria-label="Departments"
              style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--hf-space-4)' }}
            >
              {departments
                .filter((d) => !d.archived || deps.includes(d.id))
                .map((d) => (
                  <Checkbox
                    key={d.id}
                    label={d.name}
                    checked={deps.includes(d.id)}
                    onChange={(on) =>
                      setDeps((cur) => (on ? [...cur, d.id] : cur.filter((x) => x !== d.id)))
                    }
                  />
                ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 'var(--hf-space-2)' }}>
            <Button variant="primary" size="sm" loading={busy} onClick={() => void publish()}>
              Publish
            </Button>
            <Button variant="ghost" size="sm" onClick={close}>
              Cancel
            </Button>
          </div>
        </DisclosedForm>
      )}

      <ConfirmDialog
        open={!!retiring}
        title={`Retire ${retiring?.title ?? 'this policy'}?`}
        consequence={[
          'Nobody is asked to acknowledge it any more.',
          'Who acknowledged it stays on record.',
        ]}
        confirmLabel="Retire policy"
        onCancel={() => setRetiring(null)}
        onConfirm={() => retiring && void retire(retiring)}
      />
    </PCard>
  );
}
